import axios from 'axios';
import { API_DOMAIN } from '../../../components/constants';
import { mediaTextFixture } from './mediaTextFixture';

// Backend contract verified in API_REFERENCE, 24 October 2026.
export const MEDIA_TEXT_BACKEND_READY = true;
export const MEDIA_TEXT_PERMISSION = 'chat.read_photo_text';
export const mediaTextEnabled = () => MEDIA_TEXT_BACKEND_READY;
const text = value => typeof value === 'string' ? value : '';
const number = value => typeof value === 'number' && Number.isFinite(value) ? value : null;
const list = value => Array.isArray(value) ? value : [];
export const parsePolygon = value => list(value).map(point => ({ x: number(point?.x), y: number(point?.y) })).filter(point => point.x != null && point.y != null);
const safeUrl = value => { try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : null; } catch { return null; } };
export function parseMediaText(value = {}) {
  return {
    attachment_id: text(value.attachment_id), status: ['pending', 'completed', 'failed'].includes(value.status) ? value.status : 'failed',
    image_width: number(value.image_width), image_height: number(value.image_height), language: text(value.language), full_text: text(value.full_text),
    lines: list(value.lines).map(line => ({ text: text(line.text), confidence: number(line.confidence), polygon: parsePolygon(line.polygon),
      words: list(line.words).map(word => ({ text: text(word.text), polygon: parsePolygon(word.polygon) })) })),
    detections: list(value.detections).filter(item => ['vin', 'part_number', 'plate', 'phone'].includes(item.kind)).slice(0, 20)
      .map(item => ({ kind: item.kind, value: text(item.value), normalized_value: text(item.normalized_value),
        valid_check_digit: item.valid_check_digit === true, polygon: parsePolygon(item.polygon), confidence: number(item.confidence) })),
    web: { best_guess_labels: list(value.web?.best_guess_labels).map(text).filter(Boolean),
      entities: list(value.web?.entities).slice(0, 5).map(item => ({ description: text(item.description), score: Math.min(1, Math.max(0, number(item.score) || 0)) })),
      pages: list(value.web?.pages).slice(0, 5).map(item => ({ url: safeUrl(item.url), title: text(item.title) })).filter(item => item.url) },
    read_at: text(value.read_at), read_by: typeof value.read_by === 'number' ? value.read_by : null, error_code: text(value.error_code),
  };
}
export function mediaTextError(error) {
  const status = error?.response?.status || error?.status;
  const code = error?.response?.data?.code || error?.response?.data?.error?.code || error?.code;
  const seconds = mediaTextRetryAfter(error);
  if (status === 429 || code === 'RATE_LIMITED') return seconds ? `Too many reads. Try again in ${seconds} seconds.` : 'Too many reads, try again in a few minutes';
  const messages = {
    INVALID_INPUT: "This photo couldn't be read. Reopen the chat and try again.",
    PERMISSION_REQUIRED: "You don't have permission to read text in photos.",
    CONVERSATION_QUARANTINED: 'This chat is quarantined. Release it before reading text.',
    ATTACHMENT_QUARANTINED: 'This file is quarantined. Release it before reading text.',
    ATTACHMENT_NOT_FOUND: "This photo is unavailable or you can't access it.",
    UNSUPPORTED_IMAGE: "This file can't be read",
  };
  if (messages[code]) return messages[code];
  if (code === 'IMAGE_TOO_LARGE') return 'This photo is too large to read.';
  if (status === 409) return 'Already reading';
  return "Couldn't read this photo. Please try again.";
}
export function mediaTextRetryAfter(error) {
  const headers = error?.response?.headers;
  const value = Number(headers?.get?.('retry-after') ?? headers?.['retry-after'] ?? headers?.['Retry-After']);
  return Number.isFinite(value) && value > 0 ? Math.ceil(value) : 0;
}
export function createMediaTextApi({ mock = false, ready = () => MEDIA_TEXT_BACKEND_READY, client = axios } = {}) {
  const post = async (path, body) => {
    if (!(typeof ready === 'function' ? ready() : ready)) throw new Error('Media text is not available yet');
    const response = await client.post(`${API_DOMAIN}/media-text-v1/${path}`, body, { headers: { accessToken: localStorage.getItem('accessToken') } });
    if (response.data?.success !== true || !response.data.data) throw new Error('Invalid media text response');
    return response.data.data;
  };
  const mocked = () => typeof mock === 'function' ? mock() : mock;
  return {
    async read(id, force = false) {
      return parseMediaText(mocked() ? { ...mediaTextFixture, attachment_id: id } : await post('read', { attachment_id: id, force }));
    },
    async saved(ids) {
      if (mocked()) return {};
      const data = await post('by-attachments', { attachment_ids: ids });
      if (!Array.isArray(data.items)) throw new Error('Invalid saved media text response');
      return Object.fromEntries(data.items.map(item => [item.attachment_id, parseMediaText(item)]));
    },
  };
}
export const mediaTextApi = createMediaTextApi();
